"use client";

import { useState, useRef, useEffect } from "react";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";

type Message = { role: "user" | "assistant"; content: string };
type WizardFieldsPayload = {
  lifts: string;
  widthMetres: number;
  tempRoof?: boolean;
  scope: string[];
  extras?: string[];
  duration: string;
  sideAccess: string;
  parking: string;
  groundCondition: string;
  emergencyCallout?: boolean;
};
type ExtractedJob = {
  address: string;
  postcode: string;
  jobType: string;
  timingPreset: string;
  description?: string | null;
  wizardFields?: WizardFieldsPayload | null;
  client: { name: string; email: string; phone: string };
};

const WELCOME =
  "Alright mate! ScaffOps™ here. To post a job like the website form, I'll need your site address and postcode, property type, how many lifts (storeys), width in metres, what you're wrapping (e.g. front only), how long you need it, side access, parking, ground type, and when you need it — plus your contact details if you're not logged in. We can do it in a few quick questions.";

export function ScafferChatModal() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [extractedJob, setExtractedJob] = useState<ExtractedJob | null>(null);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { data: session } = useSession();
  const router = useRouter();

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", content: WELCOME }]);
    }
  }, [open, messages.length]);

  const sendMessage = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || loading) return;

    setInput("");
    setMessages((m) => [...m, { role: "user", content: trimmed }]);
    setLoading(true);
    setError("");

    const chatMessages = [
      ...messages.filter((m) => m.content).map((m) => ({ role: m.role, content: m.content })),
      { role: "user" as const, content: trimmed },
    ];

    try {
      const res = await fetch("/api/scaffer/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: chatMessages }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        content?: string;
        extractedJob?: ExtractedJob;
        error?: string;
      };

      if (!res.ok) {
        setError(data.error ?? "Something went wrong. Try again.");
        setMessages((m) => [...m, { role: "assistant", content: data.error ?? "Had a wobble there. Give it another go." }]);
        return;
      }

      const reply = data.content ?? "Alright, what else?";
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
      if (data.extractedJob) {
        setExtractedJob(data.extractedJob);
        // AI often says "I'll post it now" — actually post so it matches the promise
        postJobNow(data.extractedJob);
      }
    } catch {
      setError("Connection issue. Try again.");
      setMessages((m) => [...m, { role: "assistant", content: "Connection wobble. Try again in a sec." }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const postJobNow = async (job: ExtractedJob) => {
    if (posting) return;
    setPosting(true);
    setError("");

    try {
      const res = await fetch("/api/scaffer/post-job", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(job),
      });
      const data = (await res.json().catch(() => ({}))) as {
        jobUrl?: string;
        id?: string;
        token?: string;
        error?: string;
      };

      if (!res.ok) {
        setError(data.error ?? "Couldn't post job. Try again.");
        setPosting(false);
        return;
      }

      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: "Sorted! We're on it — getting prices to you. Check your email for the link. Cheers mate! 🍻",
        },
      ]);
      setExtractedJob(null);

      if (data.jobUrl) {
        router.push(data.jobUrl + (data.jobUrl.includes("?") ? "&" : "?") + "success=1");
      } else if (data.id && data.token) {
        router.push(`/jobs/${data.id}?token=${data.token}&success=1`);
      }
    } catch {
      setError("Couldn't post. Try again.");
    } finally {
      setPosting(false);
    }
  };

  const handlePostJob = () => {
    if (extractedJob && !posting) postJobNow(extractedJob);
  };

  const startListening = () => {
    if (typeof window === "undefined") return;
    const Ctor =
      (window as unknown as { SpeechRecognition?: unknown }).SpeechRecognition ||
      (window as unknown as { webkitSpeechRecognition?: unknown }).webkitSpeechRecognition;
    if (!Ctor) {
      setError("Voice input not supported in this browser.");
      return;
    }
    const recognition = new (Ctor as new () => {
      start: () => void;
      continuous: boolean;
      interimResults: boolean;
      lang: string;
      onstart: () => void;
      onend: () => void;
      onerror: () => void;
      onresult: (e: { results: { [i: number]: { isFinal: boolean; [j: number]: { transcript: string } } }; resultIndex: number }) => void;
    })();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = "en-GB";

    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => {
      setIsListening(false);
      setError("Couldn't hear you. Try again.");
    };
    recognition.onresult = (e: { results: { [i: number]: { isFinal: boolean; [j: number]: { transcript: string } } }; resultIndex: number }) => {
      const t = e.results[e.resultIndex];
      const transcript = t.isFinal ? t[0].transcript : "";
      if (transcript.trim()) {
        sendMessage(transcript);
      }
    };

    recognition.start();
  };

  const speak = (text: string) => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.95;
    u.lang = "en-GB";
    u.onstart = () => setSpeaking(true);
    u.onend = () => setSpeaking(false);
    window.speechSynthesis.speak(u);
  };

  const stopSpeaking = () => {
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-brand-yellow shadow-lg text-brand-dark hover:bg-brand-yellow/90 transition focus:outline-none focus:ring-2 focus:ring-brand-yellow focus:ring-offset-2"
        aria-label="Chat with ScaffOps™"
      >
        <span className="text-2xl" aria-hidden>💬</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-end justify-end p-4 sm:p-6"
          role="dialog"
          aria-labelledby="scaffer-title"
          aria-modal="true"
        >
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div className="relative flex w-full max-w-md flex-col rounded-2xl border border-zinc-200 bg-white shadow-xl max-h-[85vh] overflow-hidden">
            <div className="flex items-center justify-between border-b border-zinc-200 bg-brand-dark px-4 py-3">
              <h2 id="scaffer-title" className="font-semibold text-white">
                ScaffOps™ — your scaffolder mate
              </h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-white/80 hover:text-white p-1 -m-1"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[200px] max-h-[320px]">
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2 ${
                      m.role === "user"
                        ? "bg-brand-yellow text-brand-dark"
                        : "bg-zinc-100 text-zinc-900"
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{m.content}</p>
                    {m.role === "assistant" && m.content && (
                      <div className="mt-2 flex gap-1">
                        <button
                          type="button"
                          onClick={() => (speaking ? stopSpeaking() : speak(m.content))}
                          className="text-xs text-zinc-500 hover:text-zinc-700"
                          title={speaking ? "Stop" : "Listen"}
                        >
                          {speaking ? "🔇 Stop" : "🔊 Listen"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="rounded-2xl bg-zinc-100 px-4 py-2">
                    <span className="text-sm text-zinc-500">…</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {extractedJob && (
              <div className="border-t border-zinc-200 bg-zinc-50 px-4 py-3">
                <p className="text-sm font-medium text-zinc-700 mb-2">Ready to post:</p>
                <p className="text-sm text-zinc-600 truncate">{extractedJob.address}, {extractedJob.postcode}</p>
                <button
                  type="button"
                  onClick={handlePostJob}
                  disabled={posting}
                  className="mt-2 w-full rounded-xl bg-brand-yellow py-2.5 font-semibold text-brand-dark hover:bg-brand-yellow/90 disabled:opacity-50"
                >
                  {posting ? "Posting…" : "Post job — we're on it"}
                </button>
              </div>
            )}

            {error && (
              <div className="px-4 py-2 bg-red-50 text-red-700 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="flex gap-2 border-t border-zinc-200 p-3">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type or say it…"
                rows={1}
                className="flex-1 resize-none rounded-xl border border-zinc-300 px-3 py-2 text-sm focus:border-brand-yellow focus:outline-none focus:ring-1 focus:ring-brand-yellow/30"
                disabled={loading}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage(input);
                  }
                }}
              />
              <button
                type="button"
                onClick={startListening}
                disabled={loading || isListening}
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                  isListening ? "bg-red-500 text-white" : "bg-zinc-200 text-zinc-700 hover:bg-zinc-300"
                }`}
                title="Voice input"
                aria-label={isListening ? "Listening…" : "Start voice input"}
              >
                {isListening ? "⏹" : "🎤"}
              </button>
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-yellow text-brand-dark hover:bg-brand-yellow/90 disabled:opacity-50"
                aria-label="Send"
              >
                ↑
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
